// frontend/modules/document-type/components/DocumentTypeList.tsx
import React from 'react';
import { Empty, Spin, Alert, Pagination, Row, Col } from 'antd';
import { FileTextOutlined } from '@ant-design/icons';
import { DocumentTypeCard } from './DocumentTypeCard';
import { DocumentType } from '../types/document-type.types';

interface DocumentTypeListProps {
    documentTypes: DocumentType[];
    loading?: boolean;
    error?: string | null;
    pagination?: {
        current: number;
        pageSize: number;
        total: number;
        onChange: (page: number, pageSize?: number) => void;
    };
    onEdit?: (code: string) => void;
    onDelete?: (code: string) => void;
    onView?: (code: string) => void;
}

export const DocumentTypeList: React.FC<DocumentTypeListProps> = ({
    documentTypes,
    loading = false,
    error,
    pagination,
    onEdit,
    onDelete,
    onView
}) => {
    if (error) {
        return (
            <Alert
                message="Error Loading Document Types"
                description={error}
                type="error"
                showIcon
                style={{ marginBottom: 16 }}
            />
        );
    }

    if (loading && documentTypes.length === 0) {
        return (
            <div style={{ textAlign: 'center', padding: '50px 0' }}>
                <Spin size="large" />
            </div>
        );
    }

    if (!loading && documentTypes.length === 0) {
        return (
            <Empty
                image={<FileTextOutlined style={{ fontSize: 64, color: '#d9d9d9' }} />}
                description="No document types found"
                style={{ padding: '50px 0' }}
            />
        );
    }

    return (
        <div>
            <Spin spinning={loading}>
                <Row gutter={[16, 16]}>
                    {documentTypes.map((documentType) => (
                        <Col key={documentType.code} xs={24} sm={12} lg={8} xl={6}>
                            <DocumentTypeCard
                                documentType={documentType}
                                onEdit={onEdit}
                                onDelete={onDelete}
                                onView={onView}
                                loading={loading}
                            />
                        </Col>
                    ))}
                </Row>
            </Spin>

            {pagination && pagination.total > 0 && (
                <div style={{ textAlign: 'center', marginTop: 24 }}>
                    <Pagination
                        current={pagination.current}
                        pageSize={pagination.pageSize}
                        total={pagination.total}
                        onChange={pagination.onChange}
                        showSizeChanger
                        showQuickJumper
                        showTotal={(total, range) =>
                            `${range[0]}-${range[1]} of ${total} document types`
                        }
                        pageSizeOptions={['10', '20', '50', '100']}
                    />
                </div>
            )}
        </div>
    );
};
